import React, { useEffect, useMemo, useRef, useState } from 'react';

import { type Shown, formatValue } from '../format';
import type { Palette } from '../palette';
import type { Block, Envelope, LensDocument, LensState, LensView } from '../types';
import PrintedView from '../view/PrintedView';
import { buildViewDocument } from '../view/viewDocument';
import DetailTable from './DetailTable';

type ViewFrameProps = {
  block: Block;
  view?: LensView;
  envelopes: Array<Envelope>;
  measures: LensDocument['measures'];
  state: LensState;
  width: number;
  shown: Shown;
  setParam?: (name: string, value: string) => void;
  pick?: (column: string, member: string) => void;
  palette?: Palette;
  fullPage?: boolean;
};

const PHONE = 600;
const PRINT_DRAW_MS = 8000;

function isDark(color: string | undefined): boolean {
  const hex = /^#([0-9a-f]{6})$/i.exec(color?.trim() ?? '');
  if (!hex) return false;
  const [red, green, blue] = [0, 2, 4].map((at) => parseInt(hex[1].slice(at, at + 2), 16) / 255);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue < 0.4;
}

function themeOf(palette?: Palette) {
  if (!palette) return { scheme: 'light', tokens: {}, palette: [] };
  return {
    scheme: isDark(palette.surface) ? 'dark' : 'light',
    palette: palette.series,
    tokens: {
      '--surface': palette.surface, '--hairline': palette.hairline, '--hairline-strong': palette.hairlineStrong, '--ink': palette.ink, '--ink-2': palette.ink2, '--ink-3': palette.ink3,
      '--pos': palette.pos, '--neg': palette.neg, '--muted': palette.muted, '--shade': palette.shade, '--sans': palette.sans,
      ...Object.fromEntries(palette.series.map((color, index) => [`--chart-${index + 1}`, color])),
    },
  };
}
const FALLBACK_ROWS = 25;

function leafRows(envelope: Envelope): Envelope {
  const dims = envelope.columns.filter((column) => column.role === 'dim');
  const leaves = envelope.rows.filter((row) => row.level === undefined || row.level === dims.length);
  const ids = dims.length > 1 ? new Set(dims.filter((column) => /(^|_)id$/i.test(column.name)).map((column) => column.name)) : new Set<string>();
  return { ...envelope, columns: envelope.columns.filter((column) => !ids.has(column.name)).map((column) => column.role === 'time' ? { ...column, role: 'dim' as const } : column), rows: leaves.slice(0, FALLBACK_ROWS).map(({ level: _level, ...row }) => row), totals: dims.length ? undefined : envelope.totals };
}

function formattedValues(envelopes: Array<Envelope>, shown: Shown): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const envelope of envelopes) {
    const rows = [...envelope.rows, ...Object.values(envelope.compare ?? {}).flat(), ...Object.values(envelope.totals ?? {})];
    for (const column of envelope.columns.filter((entry) => entry.role === 'measure')) {
      const known = (out[column.name] ??= {});
      for (const row of rows) {
        const value = row[column.name];
        if (typeof value === 'number') known[String(value)] = formatValue(value, column.format, { numbers: shown.numbers, decimals: column.decimals });
      }
    }
  }
  return out;
}

export default function ViewFrame({ block, view, envelopes, measures, state, width, shown, setParam, pick, palette, fullPage }: ViewFrameProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const loads = useRef(0);
  const channel = useMemo(() => `lens-view-${block.id}-${Math.random().toString(36).slice(2)}`, [block.id]);
  const [height, setHeight] = useState(240);
  const [failed, setFailed] = useState<string>();
  const [drawn, setDrawn] = useState(false);
  const [snapshot, setSnapshot] = useState<string>();
  const printing = typeof document !== 'undefined' && Boolean(document.getElementById('lens-print'));
  const narrow = !fullPage && width > 0 && width < PHONE;
  const fallback = !view || narrow || Boolean(failed);
  const [source, setSource] = useState('');
  useEffect(() => {
    let active = true;
    setSource('');
    setFailed(undefined);
    setDrawn(false);
    setSnapshot(undefined);
    if (view) {
      const library = /\becharts\b/.test(view.js ?? '')
        ? import('echarts/dist/echarts.min.js?lens-view-source').then((module) => module.default)
        : Promise.resolve('');
      library.then((script) => {
        if (active) setSource(buildViewDocument(view, channel, script));
      }).catch((error) => { if (active) setFailed(String(error)); });
    }
    return () => { active = false; };
  }, [view, channel]);
  const payload = useMemo(
    () => ({
      type: 'lens:data',
      channel,
      data: Object.fromEntries(envelopes.map((envelope) => [envelope.query, envelope])),
      state,
      formatted: formattedValues(envelopes, shown),
      formats: Object.fromEntries([
        ...Object.entries(measures).filter(([, measure]) => measure.format).map(([id, measure]) => [id, { format: measure.format }]),
        ...envelopes.flatMap((envelope) => envelope.columns.filter((column) => column.role === 'measure').map((column) => [column.name, { format: column.format, lowerIsBetter: column.lowerIsBetter }])),
      ]),
      theme: themeOf(palette),
      snapshot: printing,
    }),
    [channel, envelopes, measures, state, shown, palette, printing],
  );

  useEffect(() => {
    if (fallback) return undefined;
    const send = () => frame.current?.contentWindow?.postMessage(payload, '*');
    const listen = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.data?.channel !== channel) return;
      if (event.data.type === 'lens:ready') send();
      else if (event.data.type === 'lens:drawn') {
        setHeight(Math.max(48, Number(event.data.height) || 0));
        setDrawn(true);
        if (printing && typeof event.data.html === 'string') setSnapshot(event.data.html);
        if (Array.isArray(event.data.unsourced) && event.data.unsourced.length) setFailed(`it printed numbers without a source (${event.data.unsourced.slice(0, 3).join(', ')})`);
      } else if (event.data.type === 'lens:error') setFailed(String(event.data.message));
      else if (event.data.type === 'lens:set') setParam?.(String(event.data.name), String(event.data.value));
      else if (event.data.type === 'lens:select') pick?.(String(event.data.column), String(event.data.value));
    };
    window.addEventListener('message', listen);
    send();
    return () => window.removeEventListener('message', listen);
  }, [fallback, payload, channel, setParam, pick, printing]);

  useEffect(() => {
    loads.current = 0;
  }, [source]);

  useEffect(() => {
    if (!printing || fallback || drawn) return undefined;
    const timer = setTimeout(() => setFailed('it did not draw in time'), PRINT_DRAW_MS);
    return () => clearTimeout(timer);
  }, [printing, fallback, drawn]);

  const onLoad = () => {
    loads.current += 1;
    if (loads.current > 1) setFailed('it tried to load another page');
  };

  useEffect(() => {
    if (failed) console.warn(`Lens view ${block.id} fell back to tables: ${failed}`);
  }, [failed, block.id]);

  if (fallback)
    return (
      <div className="view-fallback" data-lens-view-fallback={failed ? 'failed' : printing ? 'print' : 'phone'}>
        {failed ? <p className="bn">The view could not be drawn ({failed}); its data is shown as tables.</p> : null}
        {envelopes.map((envelope) => {
          const leaves = leafRows(envelope);
          const all = envelope.rows.filter((row) => row.level === undefined || row.level === envelope.columns.filter((column) => column.role === 'dim').length).length;
          return (
            <React.Fragment key={envelope.query}>
              <DetailTable envelope={leaves} shown={shown} />
              {all > leaves.rows.length ? <p className="bn">{`First ${leaves.rows.length} of ${all.toLocaleString('en-AU')} rows; the full view draws on a wider screen.`}</p> : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  if (!source) return null;
  if (snapshot && view) return <PrintedView css={view.css} html={snapshot} tokens={themeOf(palette).tokens as Record<string, string>} />;
  return <iframe className="view-frame" ref={frame} sandbox="allow-scripts" srcDoc={source} onLoad={onLoad} data-lens-view-drawn={drawn ? 'true' : undefined} style={{ height, colorScheme: themeOf(palette).scheme }} title={String(block.title ?? 'View')} />;
}
